import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link as RouterLink, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, AppBar, Toolbar, Typography, Container, Button } from '@mui/material';
import { SnackbarProvider } from 'notistack';

import { useAuth } from './contexts/AuthContext';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { SecurityTierProvider } from './contexts/SecurityTierContext';
import { SimulationProvider } from './contexts/SimulationContext';
import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/common/ProtectedRoute';
import SecurityGate from './components/security/SecurityGate';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import VersionBadge from './components/common/VersionBadge';

// Bitvavo Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PaperTradingPage from './pages/PaperTradingPage';
import StrategiesPage from './pages/StrategiesPage';
import PortfolioPage from './pages/PortfolioPage';
import TradingPage from './pages/TradingPage';
import SettingsPage from './pages/SettingsPage';
// AnalyticsPage entfernt — Funktionalität in Strategien-Seite integriert
import AcademyPage from './features/academy/AcademyPage';
import StrategyEditPage from './features/strategies/StrategyEditPage';
import StrategyWizard from './features/strategies/StrategyWizard';
import OnboardingWizard from './features/onboarding/OnboardingWizard';
import IBKRDashboard from './components/ibkr/IBKRDashboard';
import IBKRAnalysis from './components/ibkr/IBKRAnalysis';
import IBKRPositions from './components/ibkr/IBKRPositions';
import IBKROrders from './components/ibkr/IBKROrders';
import IBKRHistory from './components/ibkr/IBKRHistory';
import IBKRDepotLog from './components/ibkr/IBKRDepotLog';
import IBKRSettings from './components/ibkr/IBKRSettings';

const BacktestingPage = React.lazy(() => import('./pages/BacktestingPage'));

function AppContent() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [mode, setMode] = useState<'bitvavo' | 'ibkr'>(() => {
    return (localStorage.getItem('btrader_mode') as 'bitvavo' | 'ibkr') || 'bitvavo';
  });

  const handleModeChange = useCallback((newMode: 'bitvavo' | 'ibkr') => {
    setMode(newMode);
    localStorage.setItem('btrader_mode', newMode);
  }, []);

  const securityUser = {
    is2faEnabled: user?.is_2fa_enabled ?? false,
    hasVerifiedApiKey: user?.has_verified_api_key ?? false,
  };

  return (
    <SecurityTierProvider user={securityUser}>
      <SimulationProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* Versions-Badge global einmal gemountet — auf jeder Seite
            sichtbar (auch Login/Register), fix in der unteren linken
            Ecke. Unabhaengig von Auth-Status. */}
        <VersionBadge />
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to={mode === 'ibkr' ? '/ibkr/dashboard' : '/dashboard'} replace /> : <LoginPage />}
          />
          <Route
            path="/register"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppShell mode={mode} onModeChange={handleModeChange}>
                  <Routes>
                    {/* Bitvavo routes */}
                    <Route path="onboarding" element={<OnboardingWizard />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="paper-trading" element={<PaperTradingPage />} />
                    <Route path="strategies" element={<StrategiesPage />} />
                    <Route path="strategies/new" element={<StrategyWizard />} />
                    <Route path="strategies/:id/edit" element={<StrategyEditPage />} />
                    <Route path="academy" element={<AcademyPage />} />
                    <Route path="backtesting" element={<React.Suspense fallback={<LoadingSpinner />}><BacktestingPage /></React.Suspense>} />
                    <Route
                      path="portfolio"
                      element={
                        <SecurityGate requiredTier={2}>
                          <PortfolioPage />
                        </SecurityGate>
                      }
                    />
                    <Route
                      path="live-trading"
                      element={
                        <SecurityGate requiredTier={3}>
                          <TradingPage />
                        </SecurityGate>
                      }
                    />
                    <Route path="settings" element={<SettingsPage />} />

                    {/* IBKR routes — Live-Trading, daher Tier 3 (2FA + API-Key) */}
                    <Route
                      path="ibkr/dashboard"
                      element={
                        <SecurityGate requiredTier={2}>
                          <IBKRDashboard />
                        </SecurityGate>
                      }
                    />
                    <Route
                      path="ibkr/analysis"
                      element={
                        <SecurityGate requiredTier={2}>
                          <IBKRAnalysis />
                        </SecurityGate>
                      }
                    />
                    <Route
                      path="ibkr/positions"
                      element={
                        <SecurityGate requiredTier={2}>
                          <IBKRPositions />
                        </SecurityGate>
                      }
                    />
                    <Route
                      path="ibkr/orders"
                      element={
                        <SecurityGate requiredTier={2}>
                          <IBKROrders />
                        </SecurityGate>
                      }
                    />
                    <Route
                      path="ibkr/depot-log"
                      element={
                        <SecurityGate requiredTier={2}>
                          <IBKRDepotLog />
                        </SecurityGate>
                      }
                    />
                    <Route
                      path="ibkr/history"
                      element={
                        <SecurityGate requiredTier={2}>
                          <IBKRHistory />
                        </SecurityGate>
                      }
                    />
                    <Route
                      path="ibkr/settings"
                      element={
                        <SecurityGate requiredTier={2}>
                          <IBKRSettings />
                        </SecurityGate>
                      }
                    />

                    {/* Default redirects */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </AppShell>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>
      </SimulationProvider>
    </SecurityTierProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeContextProvider>
        <AppContent />
      </ThemeContextProvider>
    </ErrorBoundary>
  );
}

export default App;
